import React, { useEffect, useMemo } from 'react';
import { useCanUpgradeAndPayForJswFree } from '@atlassian/jira-billing/src/controllers/billing-permissions/index.tsx';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIsJswFreeEdition } from '@atlassian/jira-onboarding-core/src/controllers/is-jsw-free-edition/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { PACKAGE_NAME } from '../../common/constants.tsx';
import { useEnforceStorageModal } from '../../controllers/store/index.tsx';
import { ELIGIBLE_STORAGE_RATIO_THRESHOLD_TO_SEE_MODAL } from './constants.tsx';
import { enforceStorageLimitsModalEntryPoint } from './entrypoint.tsx';
import type { EnforceStorageLimitModalProps } from './types.tsx';
import { useJswStorageUsage } from './utils.tsx';

export const AsyncEnforceStorageLimitsModal = (): React.JSX.Element | null => {
	// We can optimise and not show the modal if the user is not on the free edition
	const isFreeEdition = useIsJswFreeEdition();
	const isAdmin = useIsAdmin();

	if (!isFreeEdition || !isAdmin) {
		return null;
	}

	return <AsyncEnforceStorageLimitsModalEligibilityChecker />;
};

const AsyncEnforceStorageLimitsModalEligibilityChecker = () => {
	// Note this API call will fire for both experiment and control cohorts
	// since it defines the cohort for the user
	const { jswStorageRatio, jswStorageLimit } = useJswStorageUsage({
		skip: false,
	});

	const hasReachedStorageRatioThreshold =
		jswStorageRatio && jswStorageRatio > ELIGIBLE_STORAGE_RATIO_THRESHOLD_TO_SEE_MODAL;

	const { canUpgradeAndPay, loading: isBillingPermissionsLoading } = useCanUpgradeAndPayForJswFree({
		caller: PACKAGE_NAME,
		skip: !jswStorageLimit || !hasReachedStorageRatioThreshold,
	});

	if (
		// usage limit & storage ratio are undefined if the request fails or not fetched yet
		!jswStorageLimit ||
		!hasReachedStorageRatioThreshold ||
		isBillingPermissionsLoading ||
		!canUpgradeAndPay
	) {
		return null;
	}

	return (
		<AsyncEnforceStorageLimitsModalInner
			jswStorageRatio={jswStorageRatio}
			jswStorageLimit={jswStorageLimit}
			canUpgradeAndPay={Boolean(canUpgradeAndPay)}
			offeringName="standard"
		/>
	);
};

export const AsyncEnforceStorageLimitsModalInner = ({
	jswStorageRatio,
	jswStorageLimit,
	canUpgradeAndPay,
}: Omit<EnforceStorageLimitModalProps, 'onClose'>): React.JSX.Element => {
	const cloudId = useCloudId();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		enforceStorageLimitsModalEntryPoint,
		{
			cloudId,
			// JSW productKey (experiment is JSW only)
			productKey: 'jira-software.ondemand',
		},
	);
	const [{ isOpen }, { setIsOpen }] = useEnforceStorageModal();
	const enforceStorageLimitsModalProps: EnforceStorageLimitModalProps = useMemo(
		() => ({
			onClose: () => {
				setIsOpen({ isOpen: 'closed', touchpoint: undefined });
			},
			jswStorageRatio,
			jswStorageLimit,
			canUpgradeAndPay,
			offeringName: 'standard',
		}),
		[jswStorageRatio, jswStorageLimit, canUpgradeAndPay, setIsOpen],
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		// Note relay is smart enough not to re-fetch the chunk if it's already loaded
		// @todo: would be great if we didn't need isOpen and be able to load() directly from sweet state
		if (isOpen === 'open') {
			entryPointActions.load();
		}
	}, [entryPointActions, isOpen]);

	return (
		<ModalEntryPointContainer
			id="async-enforce-storage-limits-modal"
			packageName={PACKAGE_NAME}
			teamName="jlove-makkuro"
			entryPointReferenceSubject={entryPointReferenceSubject}
			entryPointActions={entryPointActions}
			errorFallback="unmount"
			runtimeProps={enforceStorageLimitsModalProps}
		/>
	);
};
